import { useEffect, useState, forwardRef, useCallback, useRef } from 'react';
import TextField, { OutlinedTextFieldProps } from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import { makeStyles } from 'tss-react/mui';
import { InputProps } from '@mui/material';
import { getCurrencySymbol } from 'further-ui/utils';
import { round } from 'lodash';

const useStyles = makeStyles()({
  input: {
    '& input[type=number]': {
      MozAppearance: 'textfield',
    },
    '& input[type=number]::-webkit-outer-spin-button': {
      WebkitAppearance: 'none',
      margin: 0,
    },
    '& input[type=number]::-webkit-inner-spin-button': {
      WebkitAppearance: 'none',
      margin: 0,
    },
  },
});

interface Props extends Omit<OutlinedTextFieldProps, 'onChange' | 'variant'> {
  value?: number;
  defaultValue?: number;
  min?: number;
  max?: number;
  decimals?: number;
  minFractionDigits?: number;
  currencyStartAdornment?: boolean;
  currency?: string;
  percentEndAdornment?: boolean;
  onChange: (value: number) => void;
  zeroAsPlaceholder?: boolean;
}

const AppNumberInput = forwardRef<HTMLInputElement, Props>(
  (
    {
      min,
      max,
      decimals = 0,
      minFractionDigits,
      disabled,
      name,
      id,
      fullWidth = true,
      size = 'small',
      value,
      defaultValue = 0,
      onChange,
      helperText = '',
      error = false,
      currencyStartAdornment,
      percentEndAdornment,
      className,
      placeholder = '',
      currency = 'GBP',
      zeroAsPlaceholder = false,
      ...rest
    },
    ref,
  ) => {
    const parseNumber = (value) => {
      const updatedValue =
        typeof value === 'string'
          ? parseFloat(value.replace(/[^0-9.]/g, ''))
          : parseFloat(value);

      if (!isNaN(updatedValue)) {
        return round(updatedValue, decimals);
      }
    };

    const { classes } = useStyles();

    const formatNumber = useCallback(
      (numValue: number) => {
        return Intl.NumberFormat(
          'en-GB',
          decimals
            ? {
                minimumFractionDigits:
                  minFractionDigits !== undefined
                    ? Math.min(minFractionDigits, decimals)
                    : Number.isInteger(numValue)
                    ? 0
                    : 2,
                maximumFractionDigits: decimals,
              }
            : {},
        ).format(numValue);
      },
      [decimals, minFractionDigits],
    );

    const [fieldValue, setFieldValue] = useState(
      parseNumber(value ?? defaultValue ?? ''),
    );

    const [displayValue, setDisplayValue] = useState<string>(() => {
      const initialValue = parseNumber(value ?? defaultValue ?? '');
      if (
        zeroAsPlaceholder &&
        (initialValue === 0 || initialValue === undefined)
      ) {
        return '';
      }
      if (initialValue !== undefined) {
        return formatNumber(initialValue);
      }
      return '';
    });

    // Round and format value when the component is first mounted
    const hasInitialised = useRef(false);
    useEffect(() => {
      if (hasInitialised.current || value === undefined) {
        return;
      }
      hasInitialised.current = true;

      const parsed = parseNumber(value);
      if (parsed === undefined) {
        return;
      }

      setFieldValue(parsed);
      setDisplayValue(
        zeroAsPlaceholder && parsed === 0 ? '' : formatNumber(parsed),
      );
    }, [value, zeroAsPlaceholder, parseNumber, formatNumber]);

    // Round and format value when the input changes
    useEffect(() => {
      const newValue = value ?? defaultValue;
      if (newValue !== fieldValue) {
        setFieldValue(newValue);
        if (newValue.toString() === '-') {
          // allow a dash to indicate a blank value
          setDisplayValue(newValue.toString());
        } else if (zeroAsPlaceholder && newValue === 0) {
          setDisplayValue('');
        } else {
          setDisplayValue(formatNumber(Number(newValue)));
        }
      }
    }, [value, defaultValue, setFieldValue, zeroAsPlaceholder]);

    const inputProps: InputProps = {
      inputProps: {
        step: (10 ** decimals) ** -1,
        min,
        max,
        type: 'text',
        pattern: decimals ? '[0-9,]+([.][0-9]+)?' : '[0-9,]+',
      },
    };

    if (currencyStartAdornment) {
      inputProps.startAdornment = (
        <InputAdornment position="start">
          {getCurrencySymbol(currency)}
        </InputAdornment>
      );
    }
    if (percentEndAdornment) {
      inputProps.endAdornment = (
        <InputAdornment position="start">%</InputAdornment>
      );
    }

    return (
      <TextField
        {...rest}
        className={`${classes.input} ${className}`}
        type={'text'}
        name={name}
        placeholder={
          zeroAsPlaceholder && !placeholder ? '0' : placeholder?.toString()
        }
        id={id ?? name}
        size={size}
        fullWidth={fullWidth}
        value={displayValue}
        onChange={({ target: { value } }) => {
          setFieldValue(parseNumber(value));
          setDisplayValue(value);
        }}
        variant="outlined"
        error={error}
        helperText={helperText}
        disabled={disabled}
        onBlur={() => {
          const parsedValue = parseNumber(fieldValue);
          if (parsedValue) {
            if ((max || max === 0) && parsedValue > max) {
              onChange(max);
              setFieldValue(max);
              setDisplayValue(formatNumber(max));
            } else if ((min || min === 0) && parsedValue < min) {
              onChange(min);
              setFieldValue(min);
              setDisplayValue(formatNumber(min));
            } else {
              onChange(parsedValue);
              setFieldValue(parsedValue);
              setDisplayValue(formatNumber(parsedValue));
            }
          } else if (!isNaN(Number(defaultValue))) {
            const newValue = parseNumber(Number(defaultValue));
            setFieldValue(newValue);
            if (zeroAsPlaceholder && newValue === 0) {
              setDisplayValue('');
            } else {
              setDisplayValue(formatNumber(newValue ?? 0));
            }
            onChange(defaultValue);
          }
        }}
        onFocus={(e) => {
          const numValue = value ?? defaultValue ?? 0;
          if (zeroAsPlaceholder && numValue === 0) {
            setDisplayValue('');
            // Select all on focus to make it easy to replace
            e.target.select();
          } else {
            setDisplayValue(formatNumber(numValue));
          }
        }}
        ref={ref}
        slotProps={{
          input: inputProps,
        }}
      />
    );
  },
);

AppNumberInput.displayName = 'AppNumberInput';

export default AppNumberInput;
